import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CommonFilterSection from '../../../../components/common/CommonFilterSection';
import { fetchItemListCached, getItemListCached } from '../../../../utils/cache/itemCache';
import { fetchVendorListCached, getVendorListCached } from '../../../../utils/cache/vendorCache';
import { fetchManagerListCached, getManagerListCached } from '../../../../utils/cache/managerCache';
import { fetchDeliveryListCached, getDeliveryListCached } from '../../../../utils/cache/deliveryCache';
import {
    FilterSection as StyledFilterSection,
    DateFilterContainer,
    SectionTitle,
    StyledDatePicker,
    ButtonContainer
} from '../styles';

const FilterSection = ({
    searchParams,
    handleParamChange,
    handleSearch,
    handleReset,
    isLoading
}) => {
    const { t } = useTranslation();
    const [vendorOptions, setVendorOptions] = useState([]);
    const [managerOptions, setManagerOptions] = useState([]);
    const [itemOptions, setItemOptions] = useState([]);
    const [deliveryOptions, setDeliveryOptions] = useState([]);

    useEffect(() => {
        const hydrateOptions = async () => {
            let vendors = getVendorListCached() || [];
            let managers = getManagerListCached() || [];
            let items = getItemListCached() || [];
            let deliveries = getDeliveryListCached() || [];

            const shouldFetch = [vendors, managers, items, deliveries].some(
                (arr) => !arr || arr.length === 0
            );
            if (shouldFetch) {
                await Promise.all([
                    fetchVendorListCached(false),
                    fetchManagerListCached(false),
                    fetchItemListCached(false),
                    fetchDeliveryListCached(false)
                ]);
                vendors = getVendorListCached() || [];
                managers = getManagerListCached() || [];
                items = getItemListCached() || [];
                deliveries = getDeliveryListCached() || [];
            }

            const vendorOpts = vendors
                .map((v) => {
                    const id = v?.partner_id ?? v?.vendor_id ?? v?.company_id;
                    if (id === undefined || id === null || id === '') return null;
                    const name = String(v?.partner_name || v?.vendor_name || v?.company_name || '').trim();
                    const idStr = String(id);
                    return {
                        value: idStr,
                        label: name ? `${idStr} - ${name}` : idStr,
                        partnerId: id
                    };
                })
                .filter(Boolean)
                .sort((a, b) => a.label.localeCompare(b.label));

            const managerOpts = managers
                .map((m) => {
                    const id = m?.manager_id ?? m?.id;
                    if (id === undefined || id === null || id === '') return null;
                    const name = String(m?.manager_name || m?.name || '').trim();
                    const idStr = String(id);
                    return {
                        value: idStr,
                        label: name ? `${idStr} ${name}` : idStr
                    };
                })
                .filter(Boolean)
                .sort((a, b) => a.label.localeCompare(b.label));

            const itemOpts = items
                .map((it) => {
                    const id = it?.item_id ?? it?.id;
                    if (id === undefined || id === null || id === '') return null;
                    const itemName = it?.name || it?.item_name || '';
                    const sku = it?.sku || it?.item_code || '';
                    const label = sku && itemName
                        ? `${sku} - ${itemName}`.trim()
                        : (itemName || String(id));
                    return {
                        value: String(id),
                        label,
                        sku
                    };
                })
                .filter(Boolean);

            const sbiItems = itemOpts.filter(
                (item) => item.sku && String(item.sku).toUpperCase().startsWith('SBI')
            );
            const otherItems = itemOpts.filter(
                (item) => !item.sku || !String(item.sku).toUpperCase().startsWith('SBI')
            );
            const sortedItems = [...sbiItems, ...otherItems].map(({ sku, ...rest }) => rest);

            const deliveryOpts = deliveries
                .map((d) => {
                    const id = d?.delivery_id ?? d?.id;
                    if (id === undefined || id === null || id === '') return null;
                    const idStr = String(id);
                    const name = (d?.delivery_name || d?.name || '').trim();
                    const label = name ? `${idStr} — ${name}` : idStr;
                    const num = Number(id);
                    return {
                        value: idStr,
                        label,
                        _sort: Number.isFinite(num) ? num : idStr
                    };
                })
                .filter(Boolean)
                .sort((a, b) => {
                    if (typeof a._sort === 'number' && typeof b._sort === 'number') {
                        return a._sort - b._sort;
                    }
                    return String(a.value).localeCompare(String(b.value), undefined, {
                        numeric: true
                    });
                })
                .map(({ value, label }) => ({ value, label }));

            setVendorOptions(vendorOpts);
            setManagerOptions(managerOpts);
            setItemOptions(sortedItems);
            setDeliveryOptions(deliveryOpts);
        };

        hydrateOptions().catch(() => {});
    }, []);

    const dateFields = [
        {
            key: 'startDate',
            component: <StyledDatePicker />,
            props: {}
        },
        {
            key: 'endDate',
            component: <StyledDatePicker />,
            props: {}
        }
    ];

    const searchFields = useMemo(() => ([
        {
            name: 'subOrderId',
            label: t('invoice.columns.subOrderId'),
            type: 'text',
            xs: 12,
            sm: 6,
            md: 3
        },
        {
            name: 'vendorId',
            label: t('orders.columns.custom'),
            type: 'autocomplete',
            options: vendorOptions,
            freeSolo: false,
            xs: 12,
            sm: 6,
            md: 3
        },
        {
            name: 'managerId',
            label: t('orders.columns.name'),
            type: 'autocomplete',
            options: managerOptions,
            freeSolo: false,
            xs: 12,
            sm: 6,
            md: 3
        },
        {
            name: 'itemId',
            label: t('orders.columns.item'),
            type: 'autocomplete',
            options: itemOptions,
            freeSolo: false,
            xs: 12,
            sm: 6,
            md: 3
        },
        {
            name: 'deliveryId',
            label: t('invoice.fields.deliveryId'),
            type: 'autocomplete',
            options: deliveryOptions,
            freeSolo: false,
            xs: 12,
            sm: 6,
            md: 3
        },
        {
            name: 'dyeNo',
            label: t('invoice.columns.dyeingNo'),
            type: 'text',
            xs: 12,
            sm: 6,
            md: 3
        }
    ]), [t, vendorOptions, managerOptions, itemOptions, deliveryOptions]);

    return (
        <CommonFilterSection
            title={t('invoice.fields.searchConditions')}
            searchParams={searchParams}
            handleParamChange={handleParamChange}
            handleSearch={handleSearch}
            handleReset={handleReset}
            isLoading={isLoading}
            dateFields={dateFields}
            searchFields={searchFields}
            showDateFilter={true}
            showButtons={true}
            StyledFilterSection={StyledFilterSection}
            StyledSectionTitle={SectionTitle}
            StyledDateFilterContainer={DateFilterContainer}
            StyledButtonContainer={ButtonContainer}
        />
    );
};

export default FilterSection;
